import { post } from '../../common/axiosClient';
import { FileUploadResponse, FileUploadStatus } from './fileUpload.type';
import { API } from '../../common/constants/urls';
import { FileUploadErrorDTO } from '../project/project.type';

export const uploadFile = async (
  files: File[],
  overwriteExisting = false,
  onUpdateFileItem: (fileName: string, status: keyof typeof FileUploadStatus, errors: FileUploadErrorDTO[], progress: number) => void,
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  formData.append('overwriteExisting', overwriteExisting.toString());

  try {
    const response = await post(API.UPLOAD_FILE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        files.forEach(file => {
          onUpdateFileItem(file.name, FileUploadStatus.Uploading, [], progress);
          if (progress === 100) {
            onUpdateFileItem(file.name, FileUploadStatus.Parsing, [], progress);
          }
        });
      },
    });

    if (!response.ok) {
      // Attach the status code to the responseData to use later in your component.
      response.statusCode = response.status;
      return response;
    }

    return response.json() as Promise<FileUploadResponse>;
  } catch (error) {
    // Handle the error, you might want to update the file status to error
    files.forEach(file => {
      onUpdateFileItem(
        file.name,
        FileUploadStatus.Failed,
        [
          {
            fieldName: 'Unknown', // Provide a meaningful field name or 'Unknown'
            lineItem: '', // Optional, can be left empty if not applicable
            message: 'An error occurred', // The error message
          },
        ],
        0,
      );
    });
    // Return a rejected promise with the error message or the error itself
    return Promise.reject(error);
  }
};

export const uploadFileSequentially = async (
  files: File[],
  overwriteExisting = false,
  onUpdateFileItem: (fileName: string, status: keyof typeof FileUploadStatus, errors: FileUploadErrorDTO[], progress: number) => void,
): Promise<void> => {
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('overwriteExisting', overwriteExisting.toString());

    try {
      const response = await post(API.UPLOAD_DIVISION, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: ProgressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUpdateFileItem(file.name, FileUploadStatus.Uploading, [], progress);
          if (progress === 100) {
            onUpdateFileItem(file.name, FileUploadStatus.Parsing, [], progress);
          }
        },
      });

      const responseData = (await response) as FileUploadResponse;
      onUpdateFileItem(file.name, responseData.results[0].statusString, responseData.results[0].errors, 100);
    } catch (error) {
      console.error('Error uploading file:', error);
      onUpdateFileItem(
        file.name,
        FileUploadStatus.Failed,
        [
          {
            fieldName: 'Unknown', // Provide a meaningful field name or 'Unknown'
            lineItem: '', // Optional, can be left empty if not applicable
            message: 'Unexpected error occurred',
          },
        ],
        0,
      );
    }
  }
};

export const uploadPayAppPDF = async (
  file: File,
  onUpdateFileItem: (fileName: string, status: keyof typeof FileUploadStatus, errors: FileUploadErrorDTO[], progress: number) => void,
  projectId: string,
  drawId?: string,
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  if (drawId) {
    formData.append('drawId', drawId);
  }

  try {
    const response = await post(`${API.UPLOAD_PAYAPP}/${projectId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUpdateFileItem(file.name, FileUploadStatus.Uploading, [], progress);
        if (progress === 100) {
          onUpdateFileItem(file.name, FileUploadStatus.Parsing, [], progress);
        }
      },
    });

    const parsedData = response;
    onUpdateFileItem(file.name, FileUploadStatus.Success, [], 100);
    return parsedData;
  } catch (error: any) {
    console.error('Error uploading PayApp:', error);
    onUpdateFileItem(
      file.name,
      FileUploadStatus.Failed,
      [
        {
          fieldName: 'Unknown',
          lineItem: '',
          message: error.message || 'Unexpected error occurred',
        },
      ],
      0,
    );
    return Promise.reject(error);
  }
};
